import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Search } from "lucide-react";
import { toast } from "sonner";

const LEVELS = ["basso", "medio", "alto", "critico"];

export default function RiskMap() {
  const [processes, setProcesses] = useState([]);
  const [risks, setRisks] = useState([]);
  const [search, setSearch] = useState("");
  const [activeProcess, setActiveProcess] = useState(null);
  const [onlyActive, setOnlyActive] = useState(false);
  const [selected, setSelected] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);

  const load = async () => {
    const [{ data: p }, { data: r }] = await Promise.all([api.get("/processes"), api.get("/risks")]);
    setProcesses(p);
    setRisks(r);
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return risks.filter((r) => {
      if (activeProcess && r.catalog?.process_code !== activeProcess) return false;
      if (onlyActive && !r.is_present) return false;
      if (search) {
        const s = search.toLowerCase();
        if (!(r.catalog?.description || "").toLowerCase().includes(s) && !(r.catalog?.ade_code || "").toLowerCase().includes(s)) return false;
      }
      return true;
    });
  }, [risks, activeProcess, onlyActive, search]);

  const processStats = useMemo(() => {
    const stats = {};
    processes.forEach((p) => { stats[p.code] = { total: 0, present: 0 }; });
    risks.forEach((r) => {
      const pc = r.catalog?.process_code;
      if (!pc || !stats[pc]) return;
      stats[pc].total += 1;
      if (r.is_present) stats[pc].present += 1;
    });
    return stats;
  }, [processes, risks]);

  const updateRisk = async (risk, patch) => {
    try {
      const { data } = await api.put(`/risks/${risk.id}`, patch);
      setRisks((rs) => rs.map((r) => (r.id === risk.id ? { ...r, ...data, catalog: r.catalog } : r)));
      if (selected?.id === risk.id) setSelected((s) => ({ ...s, ...data, catalog: s.catalog }));
    } catch (e) { toast.error("Errore aggiornamento"); }
  };

  const analyzeRisk = async () => {
    if (!selected) return;
    setAiLoading(true); setAiResult(null);
    try {
      const { data } = await api.post("/ai/analyze-risk", { risk_id: selected.id });
      setAiResult(data);
    } catch (e) { toast.error("Errore AI"); }
    finally { setAiLoading(false); }
  };

  const applyAi = async () => {
    if (!aiResult?.parsed) return;
    const { impact_suggestion, probability_suggestion } = aiResult.parsed;
    await updateRisk(selected, { impact: impact_suggestion, probability: probability_suggestion });
    toast.success("Suggerimento applicato");
  };

  const lvlBadge = (lvl) => <Badge className={`badge-${lvl} rounded-sm border`} variant="outline">{lvl}</Badge>;

  return (
    <div className="space-y-6" data-testid="risk-map-page">
      <div>
        <div className="label-tag">Mappa rischi</div>
        <h1 className="font-display text-4xl font-bold tracking-tight text-slate-900 mt-1">Mappa Rischi Fiscali</h1>
        <p className="text-slate-600 text-sm mt-2">Catalogo ADE — 130 rischi su 7 processi.</p>
      </div>

      <div className="grid grid-cols-12 gap-6">
        <Card className="col-span-3 card-flat p-4" data-testid="process-sidebar">
          <div className="label-tag mb-3">Processi</div>
          <button onClick={() => setActiveProcess(null)} className={`w-full text-left text-sm py-2 px-2 rounded-sm ${!activeProcess ? "bg-slate-100 font-medium" : "hover:bg-slate-50"}`}>Tutti</button>
          {processes.map((p) => {
            const s = processStats[p.code] || { total: 0, present: 0 };
            return (
              <button key={p.code} onClick={() => setActiveProcess(p.code)} className={`w-full text-left text-sm py-2 px-2 rounded-sm flex items-center justify-between ${activeProcess === p.code ? "bg-slate-100 font-medium" : "hover:bg-slate-50"}`} data-testid={`process-filter-${p.code}`}>
                <span className="truncate">{p.name}</span>
                <span className="text-xs text-slate-500 data-num">{s.present}/{s.total}</span>
              </button>
            );
          })}
        </Card>

        <div className="col-span-9 space-y-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input placeholder="Cerca per descrizione o codice ADE…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 rounded-sm" data-testid="risk-search" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={onlyActive} onCheckedChange={setOnlyActive} data-testid="only-active-toggle" />
              <span>Solo attivi</span>
            </label>
            <Badge variant="outline" className="rounded-sm">{filtered.length} risultati</Badge>
          </div>

          <Card className="card-flat overflow-hidden" data-testid="risk-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left text-xs text-slate-600 uppercase tracking-wider">
                    <th className="py-3 px-3">ADE</th>
                    <th className="py-3 px-3">Descrizione</th>
                    <th className="py-3 px-3">Attivo</th>
                    <th className="py-3 px-3">Impatto</th>
                    <th className="py-3 px-3">Prob.</th>
                    <th className="py-3 px-3">Rischio</th>
                    <th className="py-3 px-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`risk-row-${r.catalog?.ade_code}`}>
                      <td className="py-3 px-3 data-num text-slate-600">{r.catalog?.ade_code}</td>
                      <td className="py-3 px-3 text-slate-800 max-w-md truncate" title={r.catalog?.description}>{r.catalog?.description}</td>
                      <td className="py-3 px-3"><Switch checked={r.is_present} onCheckedChange={(v) => updateRisk(r, { is_present: v })} data-testid={`risk-toggle-${r.catalog?.ade_code}`} /></td>
                      <td className="py-3 px-3">
                        {r.is_present ? (
                          <Select value={r.impact} onValueChange={(v) => updateRisk(r, { impact: v })}>
                            <SelectTrigger className="h-8 w-28 rounded-sm text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>{LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                          </Select>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="py-3 px-3">
                        {r.is_present ? (
                          <Select value={r.probability} onValueChange={(v) => updateRisk(r, { probability: v })}>
                            <SelectTrigger className="h-8 w-28 rounded-sm text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>{LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                          </Select>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="py-3 px-3">{lvlBadge(r.inherent_risk)}</td>
                      <td className="py-3 px-3">
                        <Sheet>
                          <SheetTrigger asChild>
                            <Button size="sm" variant="outline" onClick={() => { setSelected(r); setAiResult(null); }} className="rounded-sm text-xs" data-testid={`risk-detail-${r.catalog?.ade_code}`}>Dettaglio</Button>
                          </SheetTrigger>
                          <SheetContent className="w-[520px] sm:max-w-[520px]">
                            <SheetHeader>
                              <SheetTitle className="font-display">{r.catalog?.ade_code} — {r.catalog?.activity}</SheetTitle>
                            </SheetHeader>
                            <div className="mt-4 space-y-4">
                              <p className="text-sm text-slate-700">{r.catalog?.description}</p>
                              <div>
                                <Label className="label-tag">Valore economico esposto (€)</Label>
                                <Input type="number" defaultValue={r.economic_value || ""} onBlur={(e) => updateRisk(r, { economic_value: parseFloat(e.target.value) || 0 })} className="mt-1 rounded-sm" />
                              </div>
                              <div>
                                <Label className="label-tag">Note</Label>
                                <Textarea defaultValue={r.notes || ""} onBlur={(e) => updateRisk(r, { notes: e.target.value })} rows={3} className="mt-1 rounded-sm" />
                              </div>
                              <div className="border border-blue-200 bg-blue-50 p-4 rounded-sm">
                                <div className="flex items-center justify-between">
                                  <div className="text-sm font-medium text-blue-900 flex items-center gap-2"><Sparkles size={14} /> Analisi AI</div>
                                  <Button size="sm" onClick={analyzeRisk} disabled={aiLoading} className="rounded-sm bg-blue-700 hover:bg-blue-800" data-testid="ai-analyze-risk">
                                    {aiLoading ? "Analisi…" : "Analizza con AI"}
                                  </Button>
                                </div>
                                {aiResult?.parsed && (
                                  <div className="mt-3 text-xs text-blue-900 space-y-2">
                                    <div>Impatto: <b>{aiResult.parsed.impact_suggestion}</b> · Probabilità: <b>{aiResult.parsed.probability_suggestion}</b></div>
                                    <div className="text-blue-800">{aiResult.parsed.rationale}</div>
                                    <Button size="sm" onClick={applyAi} className="mt-2 rounded-sm" data-testid="ai-apply">Applica suggerimento</Button>
                                  </div>
                                )}
                                {aiResult && !aiResult.parsed && (
                                  <div className="mt-3 text-xs text-blue-900 whitespace-pre-wrap">{aiResult.raw}</div>
                                )}
                              </div>
                            </div>
                          </SheetContent>
                        </Sheet>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
